import { ClassificationResult, ImageWithMetadata, isImageWithMetadata } from '@jump-tech-frontend/domain';

export interface FileResource {
  getValue(): string;
  setValue(value: string): void;
  toJson(): any;
  getMetadata?(): ClassificationResult | null;
}

export const makeFileResource = (src: any): FileResource | null => {
  if (!src) {
    return null;
  }

  // Check for ImageWithMetadata first (has imageData and optional metadata)
  if (isImageWithMetadata(src)) {
    return ImageWithMetadataResource.from(src);
  }

  if (typeof src === 'string') {
    return ImageResource.from(src);
  } else if (typeof src === 'object') {
    // Check for attachment (has name and data)
    return AttachmentResource.from(src);
  }

  return null;
};

/**
 * An image resource is a simple string.
 */
export class ImageResource implements FileResource {
  constructor(private value: string) {}

  static from(value: string): ImageResource {
    return new ImageResource(value);
  }

  getValue(): string {
    return this.value;
  }

  setValue(value: string) {
    this.value = value;
  }

  toJson(): string {
    return this.value;
  }

  getMetadata(): ClassificationResult | null {
    return null;
  }
}

/**
 * An attachment resource is an object with a name and a data field.
 */
export class AttachmentResource implements FileResource {
  constructor(private name: string, private data: string) {}

  static from(src: any): AttachmentResource | null {
    if (src === null || !('name' in src) || !('data' in src)) {
      return null;
    }
    return new AttachmentResource(src.name, src.data);
  }

  getValue(): string {
    return this.data;
  }

  setValue(value: string) {
    this.data = value;
  }

  getName(): string {
    return this.name;
  }

  toJson(): any {
    return {
      name: this.name,
      data: this.data
    };
  }

  getMetadata(): ClassificationResult | null {
    return null;
  }
}

/**
 * An image with classification metadata
 * Used for AI-validated images that need to preserve metadata through S3 upload
 */
export class ImageWithMetadataResource implements FileResource {
  constructor(private imageData: string, private metadata: ClassificationResult | undefined) {}

  static from(src: ImageWithMetadata): ImageWithMetadataResource {
    return new ImageWithMetadataResource(src.imageData, src.metadata);
  }

  getValue(): string {
    return this.imageData;
  }

  setValue(value: string) {
    this.imageData = value;
  }

  getMetadata(): ClassificationResult | null {
    return this.metadata || null;
  }

  toJson(): ImageWithMetadata {
    return {
      imageData: this.imageData,
      metadata: this.metadata
    };
  }
}
